import React, { useEffect, useRef, useState } from "react";
import CelebrationModal from "../components/CelebrationModal.jsx";
import { detectNewMilestones, markMilestoneSeen } from "../lib/milestones.js";
import { useNotifications } from "../lib/AlertContext.jsx";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area, Cell,
} from "recharts";
import { RefreshCw, Trophy, Users, DollarSign, TrendingUp, Target, Sparkles, Crown, Calendar, Zap, FileText, ArrowUpRight, Briefcase, ChevronDown, Dumbbell, PlayCircle, BarChart3 } from "lucide-react";
import { useUzkaObrazovka } from "../lib/obrazovka.js";
import GradientHeader from "../components/GradientHeader.jsx";
import InfoTip from "../components/InfoTip.jsx";
import { Stranka, Zalozky, Dlazdice, ErrorNote, Hlaska, SkeletonCards } from "../components/ui.jsx";
import { KartaPrehledu, HlavickaDlazdice, Pilulka } from "../components/PrehledDlazdice.jsx";
import { useApiData } from "../lib/api.js";
import { useDemoMode } from "../lib/DemoModeContext.jsx";
import { fmt } from "../lib/format.js";

const MONTH_NAMES = ["Led", "Úno", "Bře", "Dub", "Kvě", "Čer", "Čvc", "Srp", "Zář", "Říj", "Lis", "Pro"];
const MONTH_FULL = ["Leden", "Únor", "Březen", "Duben", "Květen", "Červen", "Červenec", "Srpen", "Září", "Říjen", "Listopad", "Prosinec"];

function monthLabel(monthKey) {
  if (!monthKey) return "";
  const [y, m] = monthKey.split("-");
  return `${MONTH_FULL[parseInt(m, 10) - 1]} ${y}`;
}

// Largest-remainder method — zaručuje že součet zaokrouhlených procent = 100.
function calcSharePercents(values) {
  const total = values.reduce((s, v) => s + v, 0);
  if (total === 0) return values.map(() => 0);
  const exact = values.map((v) => (v / total) * 100);
  const floored = exact.map((e) => Math.floor(e));
  const used = floored.reduce((s, v) => s + v, 0);
  const remainders = exact.map((e, i) => ({ i, frac: e - Math.floor(e) }));
  remainders.sort((a, b) => b.frac - a.frac);
  const remaining = 100 - used;
  for (let k = 0; k < remaining && k < remainders.length; k++) {
    floored[remainders[k].i]++;
  }
  return floored;
}

// 14.9.2026 (fáze 6e): na telefonu se v buňkách nekreslí číslo. S ním si mřížka
// vynutila přes pět set bodů a rolovala se do stran, bez něj se dvanáct měsíců
// vejde a barva řekne to hlavní. Přesná čísla jsou na počítači a v tabulce
// Obrat po letech nad tím.
function Heatmap({ data }) {
  const uzka = useUzkaObrazovka();
  const allValues = data.flatMap((r) => r.months.map((m) => m.revenue)).filter((v) => v > 0);
  const max = Math.max(...allValues, 1);
  const avg = allValues.length > 0 ? allValues.reduce((s, v) => s + v, 0) / allValues.length : 0;
  const cellColor = (v) => {
    if (v === 0) return "bg-slate-50 text-slate-300";
    const ratio = v / max;
    if (ratio > 0.8) return "bg-emerald-500 text-white";
    if (ratio > 0.6) return "bg-emerald-400 text-white";
    if (ratio > 0.4) return "bg-emerald-200 text-emerald-900";
    if (ratio > 0.2) return "bg-amber-100 text-amber-800";
    return "bg-red-50 text-red-600";
  };
  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr>
              <th className="px-1 py-1 text-left font-medium text-slate-500">Rok</th>
              {MONTH_NAMES.map((m) => (
                <th key={m} className="px-0.5 py-1 font-medium text-slate-500 sm:px-1">
                  {uzka ? m.slice(0, 1) : m}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row) => (
              <tr key={row.year}>
                <td className="px-1 py-1 font-semibold text-slate-800 sm:px-2">{row.year}</td>
                {row.months.map((m) => (
                  <td key={m.month} className="p-0.5">
                    <div className={`rounded text-center tabular-nums font-medium ${uzka ? "py-2.5" : "py-1.5"} ${cellColor(m.revenue)}`}
                         title={`${MONTH_FULL[m.month - 1]} ${row.year}: ${fmt(m.revenue)} Kč`}>
                      {uzka ? "" : (m.revenue > 0 ? (m.revenue >= 1000 ? `${Math.round(m.revenue / 1000)}k` : Math.round(m.revenue)) : "")}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
        <span>méně</span>
        <div className="flex gap-0.5">
          <div className="w-4 h-3 bg-red-50 rounded" />
          <div className="w-4 h-3 bg-amber-100 rounded" />
          <div className="w-4 h-3 bg-emerald-200 rounded" />
          <div className="w-4 h-3 bg-emerald-400 rounded" />
          <div className="w-4 h-3 bg-emerald-500 rounded" />
        </div>
        <span>více</span>
        {avg > 0 && <span className="ml-3 demo-sensitive">Ø měsíc {fmt(Math.round(avg))} Kč</span>}
      </div>
    </div>
  );
}

// Package breakdown s rozkliknutelnou kategorií "Trénink"
// Sjednotí všech 5 tréninkových balíčků (BASIC/FLEXIBLE/GOLD/PREMIUM/Permanentka) do jedné položky.
const TRAINING_LABELS = ["Permanentka", "Koučink BASIC", "Koučink FLEXIBLE", "Koučink GOLD", "Koučink PREMIUM"];
function isTrainingLabel(label) {
  return TRAINING_LABELS.includes(label);
}
// 13.9.2026 (fáze 4f): rozpad obratu podle služby. Dřív byl název ve sloupci
// pevné šířky, proužek uprostřed a číslo s procentem se tísnilo vpravo, takže
// se na telefonu ořezávalo obojí. Teď je název s částkou nad proužkem
// a procento je pilulka, stejně jako v ročním reportu.
function PackageBreakdown({ rows }) {
  const [open, setOpen] = React.useState(false);
  if (!rows || rows.length === 0) return <div className="py-6 text-center text-sm text-slate-400">Žádná data</div>;

  const trainingItems = rows.filter((r) => isTrainingLabel(r.label));
  const otherItems = rows.filter((r) => !isTrainingLabel(r.label));
  const trainingTotal = trainingItems.reduce((s, r) => s + (r.amount || 0), 0);

  const combinedRows = [];
  if (trainingTotal > 0) combinedRows.push({ label: "Trénink", value: trainingTotal, isTraining: true });
  otherItems.forEach((r) => combinedRows.push({ label: r.label, value: r.amount }));

  const max = Math.max(...combinedRows.map((r) => r.value || 0), 1);
  const sharePcts = calcSharePercents(combinedRows.map((r) => r.value || 0));

  return (
    <div className="space-y-3">
      {combinedRows.map((r, i) => {
        const pct = Math.max(2, Math.round((r.value / max) * 100));
        const obsah = (
          <>
            <div className="flex items-baseline justify-between gap-3">
              <span className="flex min-w-0 items-center gap-2">
                {r.isTraining && (
                  <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition-transform ${open ? "" : "-rotate-90"}`} />
                )}
                <span className="truncate text-sm font-bold text-slate-800" title={r.label}>{r.label}</span>
              </span>
              <span className="shrink-0 whitespace-nowrap text-sm font-bold tabular-nums text-slate-900">{fmt(r.value)} Kč</span>
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
                <span className="block h-full rounded-full bg-brand" style={{ width: `${pct}%` }} />
              </span>
              <Pilulka>{sharePcts[i]} %</Pilulka>
              {r.isTraining && <Pilulka>{trainingItems.length} balíčků</Pilulka>}
            </div>
          </>
        );

        return (
          <React.Fragment key={r.label + i}>
            {r.isTraining ? (
              <button type="button" onClick={() => setOpen((o) => !o)} className="w-full text-left">
                {obsah}
              </button>
            ) : (
              <div>{obsah}</div>
            )}

            {r.isTraining && open && (
              <div className="space-y-2.5 rounded-xl border border-slate-200 p-3">
                {trainingItems.slice().sort((a, b) => b.amount - a.amount).map((ti) => {
                  const tPct = trainingTotal > 0 ? Math.round((ti.amount / trainingTotal) * 100) : 0;
                  const tMax = Math.max(...trainingItems.map((x) => x.amount || 0), 1);
                  const tWidth = Math.max(2, Math.round((ti.amount / tMax) * 100));
                  return (
                    <div key={ti.label}>
                      <div className="flex items-baseline justify-between gap-3">
                        <span className="truncate text-sm text-slate-700">{ti.label}</span>
                        <span className="shrink-0 whitespace-nowrap text-sm font-semibold tabular-nums text-slate-800">{fmt(ti.amount)} Kč</span>
                      </div>
                      <div className="mt-1.5 flex items-center gap-2">
                        <span className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
                          <span className="block h-full rounded-full bg-brand-400" style={{ width: `${tWidth}%` }} />
                        </span>
                        <Pilulka>{tPct} %</Pilulka>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function Stats() {
  const [view, setView] = useState("detailed");
  const { blurred } = useDemoMode();
  const { data, loading, error, refreshing, refetch } = useApiData("/api/data-total");

  // Oslavné milníky — detekce nových achievementů při načtení dat
  const [celebrationQueue, setCelebrationQueue] = useState([]);
  const [activeCelebration, setActiveCelebration] = useState(null);
  const { createNotification } = useNotifications();
  // Per-session dedupe — zabrání opakování v rámci jednoho mount cyklu
  // (StrictMode double-effect, data refresh, atd.)
  const firedMilestonesRef = useRef(new Set());

  // Pojistka — pokud někdy v minulosti běžel "jednorázový replay" experiment,
  // vyčistíme jeho artefakt (storage key milníku 100 klientů zůstal mazán),
  // a NEBUDEME ho znovu spouštět. Milník se zobrazí jen jednou (po dosažení),
  // pak je v localStorage trvale jako seen.
  useEffect(() => {
    try {
      const replayToken = "fw40_replay_v1_active100";
      // Pokud token existuje (replay už proběhl), zajistit že milník JE v localStorage jako seen.
      // Pokud chybí (zařízení které replay nikdy nepustilo), prostě nic neděláme.
      if (localStorage.getItem(replayToken)) {
        const milestoneKey = "fw40_milestone_activeClients_100";
        if (!localStorage.getItem(milestoneKey)) {
          localStorage.setItem(milestoneKey, new Date().toISOString());
        }
      }
    } catch { /* ignoruj */ }
  }, []);

  useEffect(() => {
    if (!data) return;
    const values = {
      lifetimeRevenue: data.lifetime?.revenue || 0,
      servedClients: data.lifetime?.servedClients || 0,
      activeClients: data.now?.activeNow || 0,
      mrr: data.now?.mrr || 0,
      daysInBusiness: data.funFacts?.daysSinceFirst || 0,
      totalTrainings: data.funFacts?.totalReservioTrainings || 0,
      totalTrials: data.lifetime?.trialCount || 0,
      avgDeal: data.funFacts?.avgDealLifetime || 0,
    };
    const pending = detectNewMilestones(values);
    // Per-session dedupe: pokud už jsme tento milník v této návštěvě
    // zpracovali (useEffect spustil 2× kvůli StrictMode / refresh), přeskočit.
    const freshPending = pending.filter((m) => !firedMilestonesRef.current.has(m.storageKey));
    if (freshPending.length > 0) {
      // HNED označit jako seen v localStorage + in-memory set
      freshPending.forEach((m) => {
        firedMilestonesRef.current.add(m.storageKey);
        markMilestoneSeen(m.storageKey);
      });
      setCelebrationQueue((prev) => [...prev, ...freshPending]);
      // Zapsat každý nový milník do notifikací (v listu Notifikace)
      // ID: milestone_{metricKey}_{threshold} — backend + AlertContext dedupuje
      freshPending.forEach((m) => {
        const id = `milestone_${m.metricKey}_${m.threshold}`;
        const detail = m.cfg.message(m.threshold);
        createNotification({
          id,
          typ: "milestone",
          klient: `${m.cfg.label}: ${m.cfg.formatValue(m.threshold)}`,
          detail,
        });
      });
    }
  }, [data, createNotification]);

  // Ukázat první v frontě, když není žádná aktivní
  useEffect(() => {
    if (!activeCelebration && celebrationQueue.length > 0) {
      const [next, ...rest] = celebrationQueue;
      setActiveCelebration(next);
      setCelebrationQueue(rest);
    }
  }, [celebrationQueue, activeCelebration]);

  const handleCelebrationClose = () => {
    if (activeCelebration?.storageKey) markMilestoneSeen(activeCelebration.storageKey);
    setActiveCelebration(null);
  };

  return (
    <Stranka className={blurred ? "demo-blur" : ""}>
      {/* Oslavná modalka při dosažení milníku */}
      {activeCelebration && (
        <CelebrationModal milestone={activeCelebration} onClose={handleCelebrationClose} />
      )}
      <GradientHeader
        title="Lifetime přehled"
        napoveda="Celková statistika za celou historii firmy. Detailní pohled ukáže všechny sekce, Souhrn jen hlavní čísla."
        icon={BarChart3}
        loading={loading || refreshing}
        badge={data?.lifetime ? <span className="rounded-full bg-brand-gold px-2.5 py-1 text-[11px] font-bold text-brand-dark">{data.yearsList?.length || 0} let dat</span> : null}
        akce={[{ klic: "obnovit", ikona: RefreshCw, popis: "Obnovit", onClick: refetch, disabled: refreshing }]}
      />

      {/* 13.9.2026 (fáze 4f): přepínač pohledu je pod hlavičkou jako záložky,
          stejně jako přepínání jinde v aplikaci. Dřív seděl uvnitř tmavé
          hlavičky a na telefonu ji roztahoval na dvě patra. */}
      <Zalozky
        stitek="Pohled"
        aria-label="Pohled na statistiku"
        value={view}
        onChange={setView}
        items={[
          { key: "detailed", label: "Detailní" },
          { key: "summary", label: "Souhrn" },
        ]}
      />

      {loading && <SkeletonCards count={4} />}

      {error && !loading && (
        <ErrorNote onRetry={refetch}>
          Lifetime data se nepodařilo načíst: {error}
        </ErrorNote>
      )}

      {data?.warnings && data.warnings.length > 0 && !loading && (
        <Hlaska ton="pozor" title="Některé zdroje selhaly, data jsou částečná">
          <ul className="list-inside list-disc space-y-0.5 text-xs">
            {data.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </Hlaska>
      )}

      {data && !loading && view === "summary" && <SummaryView data={data} />}
      {data && !loading && view === "detailed" && <DetailedView data={data} />}
    </Stranka>
  );
}

function SummaryView({ data }) {
  const lf = data.lifetime || {};
  const now = data.now || {};
  const forecast = data.forecast;
  const records = data.records || {};
  const currentYear = forecast?.currentYear;
  const ytdYear = currentYear ? data.byYear[currentYear] : null;
  const prevYear = currentYear ? data.byYear[String(parseInt(currentYear) - 1)] : null;
  // YoY se počítá jen mezi UZAVŘENÝMI roky (aktuální rok je nekompletní → matoucí).
  // Pro Souhrn ukážeme YoY mezi posledním uzavřeným rokem a předposledním.
  const yearsAvailable = (data.yearsList || []).filter((y) => y < currentYear);
  const lastClosedYear = yearsAvailable[yearsAvailable.length - 1];
  const lastClosedYearData = lastClosedYear ? data.byYear[lastClosedYear] : null;
  const beforeLastClosedYear = yearsAvailable.length >= 2 ? yearsAvailable[yearsAvailable.length - 2] : null;
  const beforeLastClosedYearData = beforeLastClosedYear ? data.byYear[beforeLastClosedYear] : null;
  const yoyGrowth = (lastClosedYearData && beforeLastClosedYearData && beforeLastClosedYearData.revenue > 0)
    ? Math.round(((lastClosedYearData.revenue - beforeLastClosedYearData.revenue) / beforeLastClosedYearData.revenue) * 100)
    : null;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* 13.9.2026 (fáze 4f): každá sekce je karta s vlastní fialovou hlavičkou.
          Dřív to byly holé mřížky KPI karet bez nadpisu, takže nebylo poznat,
          kde jedna část končí a druhá začíná. */}
      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={Trophy}
          nazev="Za celou historii"
          napoveda="Souhrn za celou dobu podnikání. Obrat je součet všech zaplacených faktur od první faktury v FAPI, obsloužení klienti jsou ti, kdo měli aspoň čtyři osobní tréninky."
        />
        <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 sm:p-4 lg:grid-cols-4">
          <Dlazdice
            ton="ok"
            hodnotaTon="ok"
            popisek={<>Lifetime obrat<InfoTip text="Součet všech zaplacených faktur od počátku podnikání, archiv FAPI od roku 2021 a živá data od 2026, s DPH." /></>}
            hodnota={`${fmt(lf.revenue || 0)} Kč`}
            doplnky={<>
              <Pilulka>{lf.paidCount || 0} faktur</Pilulka>
              <Pilulka>DPH {fmt(lf.revenueVat || 0)} Kč</Pilulka>
            </>}
          />
          <Dlazdice
            ton="info"
            popisek={<>Obsloužení klienti<InfoTip text="Unikátní klienti, kteří měli aspoň čtyři potvrzené osobní tréninky v Reserviu, kdykoli v historii." /></>}
            hodnota={fmt(lf.servedClients || 0)}
            doplnky={<Pilulka>{fmt(lf.uniqueCustomers || 0)} unikátních v FAPI</Pilulka>}
          />
          <Dlazdice
            ton="znacka"
            popisek={<>Aktuálně aktivní<InfoTip text="Klienti ve stavu Aktivní, tedy ti, kdo právě teď chodí." /></>}
            hodnota={fmt(now.activeNow || 0)}
            doplnky={now.oldestActiveClient ? <Pilulka><span className="demo-sensitive">nejstarší {now.oldestActiveClient.name}</span></Pilulka> : null}
          />
          <Dlazdice
            ton="ok"
            popisek={<>Ø LTV klienta<InfoTip text="Kolik u nás v průměru každý klient utratí za celou dobu spolupráce. Počítá se jako lifetime obrat dělený počtem obsloužených klientů. Prakticky říká, kolik si můžeme dovolit utratit na získání klienta. Snímek MRR najdeš v Trenérech a v ročním reportu." /></>}
            hodnota={`${fmt(lf.avgLtv || 0)} Kč`}
            doplnky={lf.avgLtv > 0 ? <Pilulka>Ø {fmt(lf.avgTrainingsPerClient)} tréninků na klienta</Pilulka> : null}
          />
        </div>
      </KartaPrehledu>

      {ytdYear && forecast && (
        <KartaPrehledu>
          <HlavickaDlazdice
            ikona={Target}
            nazev={`Letošní rok ${currentYear}`}
            napoveda="Kam se firma dostala letos a kam se za současného tempa dostane do konce roku. Meziroční růst se počítá jen mezi uzavřenými roky, letošek ještě není hotový."
          />
          <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 sm:p-4 lg:grid-cols-4">
            <Dlazdice
              ton="zlata"
              popisek="Obrat od ledna"
              hodnota={`${fmt(ytdYear.revenue || 0)} Kč`}
              doplnky={<Pilulka>{ytdYear.paidCount} faktur za {forecast.daysElapsed} dní</Pilulka>}
            />
            <Dlazdice
              ton="znacka"
              popisek={<>Odhad do konce roku<InfoTip text="Sezonně upravený odhad podle toho, jak se obrat v historii rozkládal do měsíců. Lineární odhad jen přepočítá dosavadní tempo na celý rok." /></>}
              hodnota={`${fmt(forecast.seasonalAdjusted || 0)} Kč`}
              doplnky={<Pilulka>lineárně {fmt(forecast.linearForecast)} Kč</Pilulka>}
            />
            {yoyGrowth !== null && (
              <Dlazdice
                ton={yoyGrowth >= 0 ? "ok" : "problem"}
                hodnotaTon={yoyGrowth >= 0 ? "ok" : "problem"}
                popisek={<>Meziroční růst {lastClosedYear}<InfoTip text={`Růst za poslední uzavřený rok ${lastClosedYear} proti předchozímu ${beforeLastClosedYear}. Letošní rok ${currentYear} se nepočítá, protože ještě není uzavřený.`} /></>}
                hodnota={`${yoyGrowth >= 0 ? "+" : ""}${yoyGrowth} %`}
                doplnky={<>
                  <Pilulka>{fmt(lastClosedYearData.revenue)} Kč</Pilulka>
                  <Pilulka>dřív {fmt(beforeLastClosedYearData.revenue)} Kč</Pilulka>
                </>}
              />
            )}
            <Dlazdice
              ton="neutralni"
              popisek={<>Obrat na 1 Kč reklamy<InfoTip text="Celkový obrat firmy dělený celkovými výdaji na reklamu. Zahrnuje i klientky, které nepřišly z reklamy, takže to není návratnost reklamy, ale poměr celého obratu k marketingovému rozpočtu." /></>}
              hodnota={`${lf.roi} %`}
              doplnky={<Pilulka>reklama {fmt(lf.mktSpend)} Kč</Pilulka>}
            />
            {lf.roiPlacene != null && (
              <Dlazdice
                ton={lf.roiPlacene >= 300 ? "ok" : lf.roiPlacene >= 150 ? "pozor" : "problem"}
                popisek={<>Návratnost placené reklamy<InfoTip text="Skutečná návratnost reklamy. Obrat jen těch klientek, jejichž lead má zdroj kontaktu Facebook nebo Instagram Ads, dělený celkovými výdaji na reklamu. Zdravé je nad 300 %." /></>}
                hodnota={`${lf.roiPlacene} %`}
                doplnky={<Pilulka>{fmt(lf.revenuePlacene)} Kč od {lf.placenychKlientu} klientek</Pilulka>}
              />
            )}
          </div>
        </KartaPrehledu>
      )}

      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={Crown}
          nazev="Rekordy"
          napoveda="Nejlepší čísla za celou historii firmy."
        />
        <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 sm:p-4 lg:grid-cols-4">
          {records.bestMonth && (
            <Dlazdice
              ton="zlata"
              popisek={<>Nejlepší měsíc<InfoTip text="Měsíc s nejvyšším obratem v historii." /></>}
              hodnota={`${fmt(records.bestMonth.revenue)} Kč`}
              doplnky={<Pilulka>{monthLabel(records.bestMonth.monthKey)}</Pilulka>}
            />
          )}
          {records.bestYear && (
            <Dlazdice
              ton="zlata"
              popisek={<>Nejlepší rok<InfoTip text="Rok s nejvyšším obratem." /></>}
              hodnota={records.bestYear.year}
              doplnky={<Pilulka>{fmt(records.bestYear.revenue)} Kč</Pilulka>}
            />
          )}
          {lf.topClients && lf.topClients[0] && (
            <Dlazdice
              ton="znacka"
              popisek={<>Top klient<InfoTip text="Klient s největším součtem zaplacených faktur." /></>}
              hodnota={<span className="demo-sensitive text-lg sm:text-xl">{lf.topClients[0].name}</span>}
              doplnky={<Pilulka>{fmt(lf.topClients[0].amount)} Kč</Pilulka>}
            />
          )}
          {records.topPackage && (
            <Dlazdice
              ton="info"
              popisek={<>Top balíček<InfoTip text="Tréninkový balíček s největším obratem za celou historii. Permanentka, FLEXIBLE, GOLD a PREMIUM. Drobné služby jako jídelníček nebo výživový koučink a archiv se nepočítají." /></>}
              hodnota={<span className="text-lg sm:text-xl">{records.topPackage.label}</span>}
              doplnky={<Pilulka>{fmt(records.topPackage.amount)} Kč</Pilulka>}
            />
          )}
        </div>
      </KartaPrehledu>
    </div>
  );
}

function DetailedView({ data }) {
  const lf = data.lifetime || {};
  const now = data.now || {};
  const forecast = data.forecast;
  const records = data.records || {};
  const byYear = data.byYear || {};
  const yearsList = data.yearsList || [];
  const funFacts = data.funFacts || {};

  const yearData = yearsList.map((y) => ({
    year: y,
    revenue: byYear[y]?.revenue || 0,
    mktSpend: byYear[y]?.mktSpend || 0,
    paidCount: byYear[y]?.paidCount || 0,
    leads: byYear[y]?.leads || 0,
    trialCount: byYear[y]?.trialCount || 0,
    yoyGrowthPct: byYear[y]?.yoyGrowthPct,
  }));

  const cumData = (data.monthly || []).map((m) => ({
    monthKey: m.monthKey,
    cumulative: m.cumulativeRevenue || 0,
    revenue: m.revenue || 0,
  }));

  const progressPct = forecast ? Math.round((forecast.ytdRevenue / forecast.seasonalAdjusted) * 100) : 0;

  return (
    <div className="space-y-4 sm:space-y-6">

      {/* === ZA CELOU HISTORII === */}
      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={Trophy}
          nazev="Za celou historii"
          napoveda="Souhrn za celou dobu podnikání. Obrat je součet všech zaplacených faktur od první faktury v FAPI, obsloužení klienti jsou ti, kdo měli aspoň čtyři osobní tréninky."
        />
        <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 sm:p-4 lg:grid-cols-4">
          <Dlazdice
            ton="ok"
            hodnotaTon="ok"
            popisek={<>Lifetime obrat<InfoTip text="Součet všech zaplacených faktur od počátku podnikání, archiv FAPI od roku 2021 a živá data od 2026, s DPH." /></>}
            hodnota={`${fmt(lf.revenue || 0)} Kč`}
            doplnky={<>
              <Pilulka>{lf.paidCount || 0} faktur</Pilulka>
              <Pilulka>DPH {fmt(lf.revenueVat || 0)} Kč</Pilulka>
            </>}
          />
          <Dlazdice
            ton="info"
            popisek={<>Obsloužení klienti<InfoTip text="Unikátní klienti, kteří měli aspoň čtyři potvrzené osobní tréninky v Reserviu, archiv 2024 a 2025 plus rezervace od 2026." /></>}
            hodnota={fmt(lf.servedClients || 0)}
            doplnky={<Pilulka>{fmt(funFacts.totalReservioTrainings || 0)} tréninků</Pilulka>}
          />
          <Dlazdice
            ton="znacka"
            popisek={<>Aktuálně aktivní<InfoTip text="Klienti ve stavu Aktivní, tedy ti, kdo právě teď chodí." /></>}
            hodnota={fmt(now.activeNow || 0)}
            doplnky={now.oldestActiveClient ? <Pilulka><span className="demo-sensitive">nejstarší {now.oldestActiveClient.name}, od {now.oldestActiveClient.since}</span></Pilulka> : null}
          />
          <Dlazdice
            ton="ok"
            popisek={<>Ø LTV klienta<InfoTip text="Kolik u nás v průměru každý klient utratí za celou dobu spolupráce. Počítá se jako lifetime obrat dělený počtem obsloužených klientů. Prakticky říká, kolik si můžeme dovolit utratit na získání klienta. Snímek MRR najdeš v Trenérech a v ročním reportu." /></>}
            hodnota={`${fmt(lf.avgLtv || 0)} Kč`}
            doplnky={lf.avgLtv > 0 ? <Pilulka>Ø {fmt(lf.avgTrainingsPerClient)} tréninků na klienta</Pilulka> : null}
          />
        </div>
      </KartaPrehledu>

      {/* === FIRMA V ČÍSLECH === */}
      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={Sparkles}
          nazev="Firma v číslech"
          podtitulek="zajímavé fakty z historie"
        />
        <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3 sm:p-4 lg:grid-cols-5">
          <FactTile ikona={Calendar} popisek="Dní v podnikání" hodnota={fmt(funFacts.daysSinceFirst || 0)}
            info="Počet dnů od první faktury v FAPI až do dneška." />
          <FactTile ikona={FileText} popisek="První faktura" hodnota={funFacts.firstInvoiceMonth ? monthLabel(funFacts.firstInvoiceMonth) : "—"}
            info="Měsíc a rok, kdy jsi vystavil první fakturu v FAPI." />
          <FactTile ikona={Dumbbell} popisek="Tréninků celkem" hodnota={fmt(funFacts.totalReservioTrainings || 0)}
            info="Součet všech potvrzených osobních tréninků v Reserviu, archiv 2024 a 2025 plus rezervace od 2026." />
          <FactTile ikona={PlayCircle} popisek="Trialů celkem" hodnota={fmt(lf.trialCount || 0)}
            info="Počet vystavených zkušebních tréninků za celou historii FAPI." />
          <FactTile ikona={DollarSign} popisek="Ø deal lifetime" hodnota={`${fmt(funFacts.avgDealLifetime || 0)} Kč`}
            info="Průměrná hodnota tréninkové faktury, tedy Koučink BASIC, FLEXIBLE, GOLD, PREMIUM nebo Permanentka. Výživa, B2B a ostatní se nezahrnují, aby šla vidět skutečná cena tréninkového dealu." />
        </div>
      </KartaPrehledu>

      {/* === ODHAD PRO LETOŠNÍ ROK === */}
      {forecast && (
        <KartaPrehledu>
          <HlavickaDlazdice
            ikona={Target}
            nazev={`Odhad pro rok ${forecast.currentYear}`}
            podtitulek="kam se za současného tempa dostaneš"
            napoveda="Sezonně upravený odhad používá vážený průměr toho, jak se obrat v historii rozkládal do měsíců. Lineární odhad jen přepočítá dosavadní tempo na celý rok."
          />
          <div className="flex flex-col gap-4 p-3 sm:p-4 lg:flex-row lg:items-start lg:gap-6">
            <div className="min-w-0 flex-1">
              <div className="text-sm text-slate-500">Při současném tempu vyděláš do konce roku</div>
              <div className="mt-1 text-3xl font-bold leading-tight tabular-nums text-emerald-700 sm:text-4xl">
                {fmt(forecast.seasonalAdjusted)} <span className="text-xl font-normal text-slate-500">Kč</span>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Pilulka>lineárně {fmt(forecast.linearForecast)} Kč</Pilulka>
                <Pilulka><span className="demo-sensitive">od ledna {fmt(forecast.ytdRevenue)} Kč</span></Pilulka>
              </div>
              <div className="mt-4">
                <div className="mb-1 flex items-baseline justify-between gap-2 text-xs text-slate-500">
                  <span>Splněno z odhadu</span>
                  <span className="font-bold tabular-nums text-emerald-700">{progressPct} %</span>
                </div>
                <div className="h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(progressPct, 100)}%` }} />
                </div>
                <div className="mt-1 text-xs text-slate-500 demo-sensitive">
                  {forecast.daysElapsed} z {forecast.daysElapsed + forecast.daysRemaining} dní uplynulo
                </div>
              </div>
            </div>
            <div className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3 lg:w-72 lg:grid-cols-1">
              <Dlazdice
                ton="znacka"
                popisek={<>Nových klientů<InfoTip text="Kolik nových klientů podle dosavadního tempa přibude do konce roku. Počítá se jako noví klienti od ledna dělené uplynulými dny a vynásobené počtem dní v roce. Noví jsou ti, kdo mají začátek spolupráce v tomto roce." /></>}
                hodnota={fmt(forecast.newClientsForecast)}
                doplnky={<Pilulka><span className="demo-sensitive">zatím {fmt(forecast.ytdNewClients)}</span></Pilulka>}
              />
              <Dlazdice
                ton="info"
                popisek="Leadů"
                hodnota={fmt(forecast.leadsForecast)}
                doplnky={<Pilulka><span className="demo-sensitive">zatím {fmt(forecast.ytdLeads)}</span></Pilulka>}
              />
              <Dlazdice
                ton="zlata"
                popisek="Výdajů na reklamu"
                hodnota={`${fmt(forecast.mktForecast)} Kč`}
                doplnky={<Pilulka><span className="demo-sensitive">zatím {fmt(forecast.ytdMktSpend)} Kč</span></Pilulka>}
              />
            </div>
          </div>
        </KartaPrehledu>
      )}

      {/* === OBRAT PO LETECH === */}
      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={TrendingUp}
          nazev="Obrat po letech"
          podtitulek="jak firma roste meziročně"
          napoveda="Roční obrat za jednotlivé roky. Čísla pod grafem ukazují meziroční růst proti předchozímu roku."
          odznak={`${yearsList.length} let`}
        />
        <div className="p-3 sm:p-4">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={yearData} margin={{ top: 28, right: 10, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" />
              <XAxis dataKey="year" stroke="#695C74" fontSize={13} />
              <YAxis stroke="#695C74" fontSize={12} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${Math.round(v/1000)}k` : v} />
              <Tooltip formatter={(v) => [`${fmt(v)} Kč`, "Obrat"]} />
              <Bar dataKey="revenue" radius={[6, 6, 0, 0]}>
                {yearData.map((d, i) => (
                  <Cell key={i} fill={d.yoyGrowthPct === null || d.yoyGrowthPct === undefined ? "#10b981"
                    : d.yoyGrowthPct >= 0 ? "#10b981" : "#f59e0b"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-2 flex flex-wrap justify-around gap-2 text-xs">
            {yearData.map((d) => (
              <div key={d.year} className="text-center">
                <div className="font-semibold text-slate-700">{d.year}</div>
                <div className="tabular-nums">
                  {d.yoyGrowthPct === null || d.yoyGrowthPct === undefined ? (
                    <span className="text-slate-400">základ</span>
                  ) : (
                    <span className={`font-bold ${d.yoyGrowthPct >= 0 ? "text-emerald-600" : "text-rose-600"}`}>
                      {d.yoyGrowthPct >= 0 ? "+" : ""}{d.yoyGrowthPct} %
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </KartaPrehledu>

      {/* === DALŠÍ ČÍSLA PO LETECH === */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <KartaPrehledu>
          <HlavickaDlazdice ikona={FileText} nazev="Faktury po letech" napoveda="Počet zaplacených faktur za jednotlivé roky." />
          <div className="p-3 sm:p-4">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={yearData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" />
                <XAxis dataKey="year" stroke="#695C74" fontSize={12} />
                <YAxis stroke="#695C74" fontSize={12} />
                <Tooltip />
                <Bar dataKey="paidCount" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Faktur" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </KartaPrehledu>

        <KartaPrehledu>
          <HlavickaDlazdice ikona={Zap} nazev="Reklama po letech" napoveda="Celkové výdaje na Meta Ads v jednotlivých letech." />
          <div className="p-3 sm:p-4">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={yearData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" />
                <XAxis dataKey="year" stroke="#695C74" fontSize={12} />
                <YAxis stroke="#695C74" fontSize={12} tickFormatter={(v) => v >= 1000 ? `${Math.round(v/1000)}k` : v} />
                <Tooltip formatter={(v) => `${fmt(v)} Kč`} />
                <Bar dataKey="mktSpend" fill="#f59e0b" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </KartaPrehledu>

        <KartaPrehledu>
          <HlavickaDlazdice ikona={Users} nazev="Leady po letech" napoveda="Počet leadů v evidenci, ta existuje od roku 2026. Pro starší roky je nula, protože tenkrát ještě nebyla." />
          <div className="p-3 sm:p-4">
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={yearData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" />
                <XAxis dataKey="year" stroke="#695C74" fontSize={12} />
                <YAxis stroke="#695C74" fontSize={12} />
                <Tooltip />
                <Bar dataKey="leads" fill="#7C5DB0" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </KartaPrehledu>
      </div>

      {/* === REKORDY === */}
      <KartaPrehledu>
        <HlavickaDlazdice ikona={Crown} nazev="Rekordy" podtitulek="nejlepší čísla za celou historii" />
        <div className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-2 sm:p-4 lg:grid-cols-4">
          {records.bestMonth && (
            <Dlazdice
              ton="zlata"
              popisek={<>Nejlepší měsíc<InfoTip text="Měsíc s nejvyšším obratem v celé historii FAPI." /></>}
              hodnota={`${fmt(records.bestMonth.revenue)} Kč`}
              doplnky={<Pilulka>{monthLabel(records.bestMonth.monthKey)}</Pilulka>}
            />
          )}
          {records.bestYear && (
            <Dlazdice
              ton="znacka"
              popisek={<>Nejlepší rok<InfoTip text="Rok s nejvyšším obratem." /></>}
              hodnota={records.bestYear.year}
              doplnky={<>
                <Pilulka>{fmt(records.bestYear.revenue)} Kč</Pilulka>
                <Pilulka>{records.bestYear.paidCount} faktur</Pilulka>
              </>}
            />
          )}
          {now.oldestActiveClient && (
            <Dlazdice
              ton="info"
              popisek={<>Nejstarší aktivní<InfoTip text="Aktivní klient s nejstarším datem začátku spolupráce." /></>}
              hodnota={<span className="demo-sensitive text-lg sm:text-xl">{now.oldestActiveClient.name}</span>}
              doplnky={<Pilulka>od {now.oldestActiveClient.since}</Pilulka>}
            />
          )}
          {records.topPackage && (
            <Dlazdice
              ton="ok"
              popisek={<>Nejžádanější balíček<InfoTip text="Tréninkový balíček s největším obratem za celou historii. Permanentka, FLEXIBLE, GOLD a PREMIUM. Drobné služby jako jídelníček nebo výživový koučink a archivní Ostatní se nepočítají." /></>}
              hodnota={<span className="text-lg sm:text-xl">{records.topPackage.label}</span>}
              doplnky={<Pilulka>{fmt(records.topPackage.amount)} Kč</Pilulka>}
            />
          )}
        </div>
      </KartaPrehledu>

      {/* === TOP 20 KLIENTŮ === */}
      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={Trophy}
          nazev="Top klienti"
          napoveda="Klienti, kteří měli aspoň jednu fakturu za tréninkový koučink, tedy BASIC, FLEXIBLE, GOLD, PREMIUM nebo Permanentku. Obrat zahrnuje pouze tréninkové faktury, výživa, jídelníčky a B2B se nepočítají."
          odznak={(lf.topClients || []).length > 0 ? `${lf.topClients.length} nejlepších` : false}
        />
        {(lf.topClients || []).length > 0 ? (
          <div className="divide-y divide-slate-100">
            {lf.topClients.map((c, i) => {
              const maxAmount = lf.topClients[0].amount;
              const pct = Math.max(2, Math.round((c.amount / maxAmount) * 100));
              const medaile = i === 0 ? "bg-brand-gold text-brand-dark"
                : i === 1 ? "bg-slate-200 text-slate-700"
                : i === 2 ? "bg-amber-100 text-amber-800"
                : "bg-slate-100 text-slate-500";
              return (
                <div key={i} className="flex items-center gap-3 px-4 py-2.5 sm:px-5">
                  <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold tabular-nums ${medaile}`}>
                    {i + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-slate-900 demo-sensitive" title={c.name}>{c.name}</span>
                      <span className="shrink-0 whitespace-nowrap text-sm font-bold tabular-nums text-emerald-700">{fmt(c.amount)} Kč</span>
                    </div>
                    <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                      <div className="h-full rounded-full bg-emerald-500" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : <div className="py-6 text-center text-sm text-slate-400">Žádná data</div>}
      </KartaPrehledu>

      {/* === OBRAT PODLE SLUŽBY === */}
      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={Briefcase}
          nazev="Obrat podle služby"
          napoveda="Sjednocené kategorie. Trénink zahrnuje Koučink BASIC, FLEXIBLE, GOLD, PREMIUM a Permanentku, klepnutím se rozbalí. Výživa je jídelníček, výživový koučink a konzultace. B2B je den zdraví a firemní faktury nad 30 tisíc. Ostatní je drobný archiv."
        />
        <div className="p-3 sm:p-4">
          <PackageBreakdown rows={lf.packageBreakdown || []} />
        </div>
      </KartaPrehledu>

      {/* === KUMULATIVNÍ OBRAT === */}
      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={ArrowUpRight}
          nazev="Kumulativní obrat"
          podtitulek="růst firmy v čase"
          napoveda={`Součet obratu přes čas, od první faktury až doteď. První faktura v FAPI byla v ${funFacts.firstInvoiceMonth ? monthLabel(funFacts.firstInvoiceMonth) : "neznámém měsíci"}.`}
        />
        <div className="p-3 sm:p-4">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={cumData}>
              <defs>
                <linearGradient id="cumGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#10b981" stopOpacity={0.5} />
                  <stop offset="100%" stopColor="#10b981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#EAE6EF" />
              <XAxis dataKey="monthKey" stroke="#695C74" fontSize={11}
                tickFormatter={(mk) => mk.substring(0, 7)}
                interval={Math.max(0, Math.floor(cumData.length / 10))} />
              <YAxis stroke="#695C74" fontSize={12} tickFormatter={(v) => v >= 1000000 ? `${(v/1000000).toFixed(1)}M` : v >= 1000 ? `${Math.round(v/1000)}k` : v} />
              <Tooltip formatter={(v) => `${fmt(v)} Kč`} labelFormatter={(mk) => monthLabel(mk)} />
              <Area type="monotone" dataKey="cumulative" stroke="#10b981" strokeWidth={2.5} fill="url(#cumGrad)" name="Kumulativně" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </KartaPrehledu>

      {/* === SEZÓNNÍ HEATMAPA === */}
      <KartaPrehledu>
        <HlavickaDlazdice
          ikona={Calendar}
          nazev="Sezónní přehled"
          podtitulek="který měsíc vydělává nejvíc"
          napoveda="Obrat po měsících a letech. Tmavší zelená je vyšší obrat, prázdné políčko znamená, že v tom měsíci nic nepřišlo."
        />
        <div className="p-3 sm:p-4">
          <Heatmap data={data.heatmap || []} />
        </div>
      </KartaPrehledu>

    </div>
  );
}

// 13.9.2026 (fáze 4f): dlaždice faktu. Dřív měla barevnou hlavičku s jedním
// z pěti různých přechodů, takže pět dlaždic vedle sebe mělo pět barev a žádná
// z nich nic neznamenala. Teď je tichá, popisek nahoře, číslo pod ním.
function FactTile({ ikona: Ikona, popisek, hodnota, info }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-start gap-1.5 text-[11px] font-bold uppercase leading-tight tracking-wider text-slate-400">
        {Ikona && <Ikona className="mt-px h-3.5 w-3.5 shrink-0" />}
        <span className="min-w-0 flex-1">{popisek}</span>
        {info && <InfoTip text={info} />}
      </div>
      <div className="mt-1 break-words text-xl font-bold tabular-nums text-slate-900 sm:text-2xl">
        {hodnota}
      </div>
    </div>
  );
}
